'use client';

import { c as _c } from "react/compiler-runtime";
import { ConversationsContextProvider } from '@shared/utils/context/ConversationsContext';
import { DatabaseProvider } from '@shared/utils/context/DatabaseContext';
import { LocalPreferenceContextProvider } from '@shared/utils/context/LocalPreferenceContext';
import { NavigatorContextProvider } from '@shared/utils/context/NavigatorContext';
import { UserContextProvider } from '@shared/utils/context/UserContext';
import toast from '@shared/utils/helpers/toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/next';
import dynamic from 'next/dynamic';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { CrossPostProgressProvider } from '../../hooks/useCrossPostProgress';
import { JobTerminalProvider } from '../../hooks/useJobTerminal';
import { RunnersProvider } from '../../hooks/useRunners';
import { SearchScopeProvider } from '../../hooks/useSearchScope';
import { client } from '../../utils/trpc';
import AppWidgets from '../../widgets/AppWidgets';
import AuthWidgets from '../../widgets/AuthWidgets';
import CommonWidgets from '../../widgets/CommonWidgets';
import { WidgetsProvider } from '../../widgets/WidgetsContext';
import ErrorBoundary from '../common/ErrorBoundary';
import { ChatWorkspaceProvider } from '../pages/chat/ChatWorkspaceContext';

// Mounted globally but each renders null until a signed-in condition fires (a
// database error, an in-flight cross-post, a runner job settling). Static
// imports put all three in the root chunk that every anonymous marketing
// visitor downloads.
const DatabaseErrorModal = dynamic(() => import('../database/DatabaseErrorModal'), {
  ssr: false
});
const CrossPostProgressPill = dynamic(() => import('../posts/CrossPostProgressPill'), {
  ssr: false
});
const JobFinishNotifier = dynamic(() => import('../common/JobFinishNotifier'), {
  ssr: false
});
// The store-password prompt, global so a command run from any session in any
// space can reach the owner wherever they are.
const SecretsAuthGate = dynamic(() => import('../common/SecretsAuthGate'), {
  ssr: false
});
// The runner owner's yes or no to a member's dangerous job call, global for
// the same reason: the call waits on the owner wherever they are.
const DangerousApprovalGate = dynamic(() => import('../common/DangerousApprovalGate'), {
  ssr: false
});

// React Query Devtools: dev-only. Loaded via next/dynamic so the chunk is never
// requested in production (the env gate below keeps it unmounted, so its bundle
// stays out of prod HTML/network). Previously rendered unconditionally.
const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools').then(m => m.ReactQueryDevtools), {
  ssr: false
});
const customRetry = (failureCount: number, error: any) => {
  // Check tRPC error code (from error.data.code) or HTTP status code
  const trpcCode = error.data?.code;
  const httpStatus = error.data?.httpStatus;

  // Do not retry for auth errors (401) - prevents infinite loop on logout
  if (trpcCode === 'UNAUTHORIZED' || httpStatus === 401) {
    return false;
  }

  // Do not retry for bad request errors (400)
  if (trpcCode === 'BAD_REQUEST' || httpStatus === 400) {
    toast(error.message, '', 'error');
    return false;
  }

  // Do not retry for forbidden errors (403)
  if (trpcCode === 'FORBIDDEN' || httpStatus === 403) {
    return false;
  }

  // Default retry behavior for other errors (network issues, 500s, etc.)
  return failureCount < 3;
};
export const Provider: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(16);
  const {
    children
  } = t0;
  const [queryClient] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp4, t1);
  const reportErrorToServer = _temp5;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AuthWidgets />;
    t4 = <CommonWidgets />;
    t5 = <AppWidgets />;
    t6 = <DatabaseErrorModal />;
    t7 = <CrossPostProgressPill />;
    t8 = <JobFinishNotifier />;
    t9 = <SecretsAuthGate />;
    t10 = <DangerousApprovalGate />;
    t11 = <Analytics />;
    $[2] = t10;
    $[3] = t11;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t10 = $[2];
    t11 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t12;
  if ($[11] !== children) {
    t12 = <UserContextProvider initialUser={null}><DatabaseProvider reportErrorToServer={reportErrorToServer} client={client} disableHashing={true}><LocalPreferenceContextProvider><NavigatorContextProvider><ConversationsContextProvider><ChatWorkspaceProvider><WidgetsProvider><RunnersProvider><JobTerminalProvider><CrossPostProgressProvider><SearchScopeProvider>{children}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}</SearchScopeProvider></CrossPostProgressProvider></JobTerminalProvider></RunnersProvider></WidgetsProvider></ChatWorkspaceProvider></ConversationsContextProvider></NavigatorContextProvider></LocalPreferenceContextProvider></DatabaseProvider></UserContextProvider>;
    $[11] = children;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== queryClient || $[14] !== t12) {
    t13 = <ErrorBoundary><QueryClientProvider client={queryClient}>{t2}{t12}</QueryClientProvider></ErrorBoundary>;
    $[13] = queryClient;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  return t13;
};
function _temp() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: customRetry
      }
    }
  });
}
function _temp2(event) {
  const error = event.error;
  const message = event.message?.toLowerCase() || "";
  if (error?.name === "AbortError" || message.includes("aborted") && message.includes("media resource") || message.includes("aborted") && message.includes("fetching process")) {
    event.preventDefault();
    return false;
  }
}
function _temp3(event_0) {
  const reason = event_0.reason;
  const reasonString = typeof reason === "string" ? reason.toLowerCase() : "";
  if (reason?.name === "AbortError" || reasonString.includes("aborted") && reasonString.includes("media resource") || reasonString.includes("aborted") && reasonString.includes("fetching process")) {
    event_0.preventDefault();
    return false;
  }
}
function _temp4() {
  const handleError = _temp2;
  const handleUnhandledRejection = _temp3;
  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
}
async function _temp5(error_0, stack) {
  ;
  try {
    await client.users.error.mutate({
      error: error_0,
      stack,
      source: "client"
    });
  } catch (t0) {
    const err = t0;
  }
}